import { Stack, Text, textColors, textVariants, vars } from '@etg/wings';
import { useProperty } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { useController, useFormContext } from 'react-hook-form';
import { passengerFields, PASSENGERS_FORM_SECTION } from '../../constants/formFields';
import { MAX_CHILD_AGE, MIN_CHILD_AGE } from '../../constants/passengers';
import {
  changeValueWithOperand,
  isValidAdultSelection,
  isValidChildSelection,
  isValidInfantSelection,
} from '../../utils/passengers';
import ButtonControls from './PassengerButtonControls';

const rowStyles = css`
  font-size: 1rem;
  padding: 1rem;
`;

const borderStyles = css`
  border-top: 0.0625rem solid ${vars.colors.divider};

  &:last-child {
    border-bottom: 0.0625rem solid ${vars.colors.divider};
  }
`;

const controlWrapperStyles = css`
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;
`;

export interface PassengerRowProps {
  ageSpan?: string;
  constraints?: {
    maxNumberOfTravelers: number;
    isDisabled: boolean;
  };
  description?: string;
  fieldName: string;
  label: string;
  passengerIndex?: number;
  toggleConstraints?: {
    add: { isDisabled: boolean };
    subtract: { isDisabled: boolean };
  };
}

const PassengerRow = ({
  ageSpan,
  constraints,
  description,
  fieldName,
  label,
  passengerIndex,
  toggleConstraints,
}: PassengerRowProps) => {
  const { p } = useProperty();
  const { control, watch, getValues, setValue } = useFormContext();
  const {
    field: { onChange, name },
  } = useController({ control, name: fieldName });
  const selectedValue = watch(name);
  const selectedPassengers = getValues(PASSENGERS_FORM_SECTION);
  const useAlternateLayout = p('SearchForm.Passengers.AlternateInputLayout.Enabled');

  const handleOnChange = (operand: 'add' | 'subtract') => () => {
    switch (fieldName) {
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`:
        if (typeof passengerIndex === 'number') {
          const newValue = changeValueWithOperand(selectedValue[passengerIndex], operand);
          const currentChildrenAges = selectedValue;

          if (newValue < MIN_CHILD_AGE) {
            currentChildrenAges[passengerIndex] = MAX_CHILD_AGE;
          } else if (newValue > MAX_CHILD_AGE) {
            currentChildrenAges[passengerIndex] = MIN_CHILD_AGE;
          } else {
            currentChildrenAges[passengerIndex] = newValue;
          }

          onChange(currentChildrenAges);
        }
        break;
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.ADULTS}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidAdultSelection(newValue, selectedPassengers, constraints);
        if (changeIsValid) {
          const newAmountOfInfants = Math.min(newValue, selectedPassengers.infants);
          setValue(`${PASSENGERS_FORM_SECTION}.${passengerFields.INFANTS}`, newAmountOfInfants);
          onChange(newValue);
        }
        break;
      }
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidChildSelection(newValue, selectedPassengers, constraints);
        if (changeIsValid) {
          onChange(newValue);
        }
        break;
      }
      case `${PASSENGERS_FORM_SECTION}.${passengerFields.INFANTS}`: {
        const newValue = changeValueWithOperand(selectedValue, operand);
        const changeIsValid = isValidInfantSelection(newValue, selectedPassengers);
        if (changeIsValid) {
          onChange(newValue);
        }
        break;
      }
      default: {
        onChange(changeValueWithOperand(selectedValue, operand));
      }
    }
  };

  const fieldId =
    name === `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`
      ? `${name}-${passengerIndex! + 1}`
      : name;
  const inputId = `${fieldId}-passengers-input`;
  const displayValue =
    fieldName === `${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`
      ? selectedValue[passengerIndex!]
      : selectedValue;

  return (
    <Stack className={cx(rowStyles, { [borderStyles]: !useAlternateLayout })}>
      <div className={controlWrapperStyles}>
        <label
          aria-describedby={description ? `passenger-${name}-description` : undefined}
          htmlFor={inputId}
        >
          <Stack as="span" spacing={0}>
            <span>{label}</span>
            {useAlternateLayout && ageSpan && (
              <Text color={textColors.SECONDARY} variant={textVariants._14}>
                {ageSpan}
              </Text>
            )}
          </Stack>
        </label>
        <ButtonControls
          displayValue={displayValue}
          fieldId={fieldId}
          label={label}
          onChange={handleOnChange}
          toggleConstraints={toggleConstraints}
        />
      </div>
      {description && (
        <Text
          as="p"
          color={textColors.SECONDARY}
          id={`passenger-${name}-description`}
          variant={textVariants._14}
        >
          {description}
        </Text>
      )}
    </Stack>
  );
};

export default PassengerRow;
